// Vendor Debt — settlement tracking screen.
//
// Admin-only, routed at /vendor-debt/:vendorId. This is a pure MONEY ledger:
// it tracks what is owed to a vendor and what has been repaid, and is
// deliberately unrelated to stock, product items, procurement or vendor
// purchases.
//
// Shows Total Debt / Paid / Unpaid as a summary header, the paginated
// transaction list below it (newest first), and an "Add Entry" dialog.
// Entries are immutable — there is no edit or delete; a mistake is corrected
// by posting the opposite entry. Mirrors CustomerCreditLedger.tsx's
// DataTable + offset/limit/page pagination pattern.
import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, MoreVertical } from "lucide-react";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Input } from "ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "ui/dropdown-menu";
import ApparelMenu from "../SubComponents/apparelMenu";
import VendorDebtAttachmentViewer from "./VendorDebtAttachmentViewer";
import VendorDebtAttachmentSheet from "./VendorDebtAttachmentSheet";
import {
    getVendorDebtLedgerAction,
    createVendorDebtEntryAction,
} from "../../Store/actions/apparelAction";

const PAGE_LIMIT = 10;
const ENTRY_TYPE_DEBT = "DEBT";
const ENTRY_TYPE_PAYMENT = "PAYMENT";

const formatAmount = (value: any) => `₹${Number(value ?? 0).toLocaleString("en-IN")}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const VendorDebtLedger = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // Arriving from Audit Log. Unlike the Credit Ledger — which can only
    // highlight "the newest row" and therefore flags the wrong transaction
    // when an old audit entry is clicked — the audit row carries this exact
    // ledger row's uniqueKey, so the right row is highlighted every time.
    const highlightDebtId: string | undefined = location.state?.highlightDebtId;

    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const {
        getVendorDebtLedgerData,
        getVendorDebtLedgerOnLoad,
        createVendorDebtEntryOnLoad,
        createVendorDebtEntryData,
    } = apparelReducerState;

    const ledgerResult = getVendorDebtLedgerData?.getVendorDebtLedger;
    const ledgerRows = ledgerResult?.vendorDebtLedger || [];
    const ledgerPage = ledgerResult?.page;

    const [offset, setOffset] = React.useState(0);
    // "" = All. Values match tbl_vendordebtledger.entryType (DEBT/PAYMENT).
    const [entryTypeFilter, setEntryTypeFilter] = React.useState("");

    const getLedgerApi = React.useCallback((nextOffset: number, entryType: string, seekDebtId?: string) => {
        dispatch(getVendorDebtLedgerAction({
            vendorId,
            entryType: entryType || undefined,
            // When set, the service ignores `offset` and returns whichever page
            // actually contains this row — the audited entry may be far down a
            // newest-first list.
            vendorDebtId: seekDebtId,
            limit: PAGE_LIMIT,
            offset: nextOffset,
        }));
    }, [dispatch, vendorId]);

    // Only the FIRST load honours highlightDebtId; once the user pages or
    // filters, the server must respect their offset again.
    const seekConsumed = React.useRef(false);

    React.useEffect(() => {
        const seekDebtId = (!seekConsumed.current && highlightDebtId) ? highlightDebtId : undefined;
        seekConsumed.current = true;
        getLedgerApi(offset, entryTypeFilter, seekDebtId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, entryTypeFilter, vendorId]);

    // The server may have landed us on a different page than `offset` says, so
    // derive the next offset from the page the server actually returned.
    const onPageChange = (nextPage: number) => {
        const next = ((nextPage * PAGE_LIMIT) - PAGE_LIMIT === 0) ? 0 : (nextPage * PAGE_LIMIT) - PAGE_LIMIT;
        if (next === offset) {
            getLedgerApi(next, entryTypeFilter);
            return;
        }
        setOffset(next);
    };

    // Any filter change has to reset paging — staying on page 4 of a narrower
    // result set would render an empty table.
    const onEntryTypeFilterChange = (value: string) => {
        setEntryTypeFilter(value === "ALL" ? "" : value);
        setOffset(0);
    };

    const outstanding = Number(ledgerResult?.outstanding ?? 0);

    const [proofViewerOpen, setProofViewerOpen] = React.useState(false);
    const [proofSheetOpen, setProofSheetOpen] = React.useState(false);
    const [proofEntityId, setProofEntityId] = React.useState<any>(null);

    const onViewProof = (row: any) => {
        setProofEntityId(row?.vendorDebtId);
        setProofViewerOpen(true);
    };

    const onAttachProof = (row: any) => {
        setProofEntityId(row?.vendorDebtId);
        setProofSheetOpen(true);
    };

    const [entryOpen, setEntryOpen] = React.useState(false);
    const [entryType, setEntryType] = React.useState(ENTRY_TYPE_DEBT);
    const [entryAmount, setEntryAmount] = React.useState("");
    const [entryDate, setEntryDate] = React.useState(todayStr());
    const [entryNotes, setEntryNotes] = React.useState("");
    const [entrySubmitted, setEntrySubmitted] = React.useState(false);

    const openEntry = () => {
        setEntryType(ENTRY_TYPE_DEBT);
        setEntryAmount("");
        setEntryDate(todayStr());
        setEntryNotes("");
        setEntryOpen(true);
    };

    const submitEntry = () => {
        if (!entryAmount || Number(entryAmount) <= 0) {
            toast.error("Enter a valid amount");
            return;
        }
        if (!entryDate) {
            toast.error("Select a transaction date");
            return;
        }
        // A payment can never exceed the unpaid balance. The service enforces
        // this too (inside the row lock, which is the authoritative check) —
        // this is only so the user finds out before a round trip.
        if (entryType === ENTRY_TYPE_PAYMENT) {
            if (outstanding <= 0) {
                toast.error("There is no unpaid debt to pay against this vendor");
                return;
            }
            if (Number(entryAmount) > outstanding) {
                toast.error(`Payment cannot exceed the unpaid debt of ${formatAmount(outstanding)}`);
                return;
            }
        }
        // The <input type="date" max> attribute is only an advisory in some
        // browsers, so the same rule is enforced here too.
        if (entryDate > todayStr()) {
            toast.error("Transaction date cannot be in the future");
            return;
        }
        dispatch(createVendorDebtEntryAction({
            vendorId,
            entryType,
            amount: Number(entryAmount),
            transactionDate: entryDate,
            notes: entryNotes,
        }));
        setEntrySubmitted(true);
    };

    React.useEffect(() => {
        if (createVendorDebtEntryOnLoad == false && entrySubmitted == true) {
            setEntrySubmitted(false);
            // The saga stores result.result.data.data (same as the other vendor
            // sagas), so the mutation field sits directly on the stored object.
            const result = createVendorDebtEntryData?.createVendorDebtEntry;
            if (result?.status == 1) {
                toast.success(entryType === ENTRY_TYPE_DEBT ? "Debt recorded" : "Payment recorded");
                setEntryOpen(false);
                // A new entry is always the newest row, so jump back to page 1
                // rather than refetching whatever page is currently shown.
                if (offset === 0) {
                    getLedgerApi(0, entryTypeFilter);
                } else {
                    setOffset(0);
                }
            } else {
                toast.error(result?.message || "Failed to record entry");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createVendorDebtEntryOnLoad, entrySubmitted]);

    const columns = React.useMemo<ColumnDef<any, any>[]>(() => [
        {
            id: "transactionDate",
            header: "Date",
            cell: ({ row }) => moment(row.original?.transactionDate).format("DD-MMM-YYYY"),
        },
        {
            id: "entryType",
            header: "Type",
            cell: ({ row }) => (
                <Badge variant={row.original?.entryType === ENTRY_TYPE_DEBT ? "destructive" : "success"}>
                    {row.original?.entryType === ENTRY_TYPE_DEBT ? "Debt" : "Payment"}
                </Badge>
            ),
        },
        {
            id: "amount",
            header: "Amount",
            cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original?.amount)}</span>,
        },
        {
            id: "newBalance",
            header: "Unpaid After",
            cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original?.newBalance)}</span>,
        },
        {
            id: "notes",
            header: "Notes",
            cell: ({ row }) => row.original?.notes || "-",
        },
        {
            id: "createdBy",
            header: "Recorded By",
            cell: ({ row }) => row.original?.createdBy || "-",
        },
        {
            id: "attachment",
            header: "Attachment",
            cell: ({ row }) => (
                Number(row.original?.attachmentCount) > 0 ? (
                    <button
                        type="button"
                        onClick={() => onViewProof(row.original)}
                        className="text-primary hover:underline"
                    >
                        View
                    </button>
                ) : (
                    <span className="text-muted-foreground">-</span>
                )
            ),
        },
        {
            id: "action",
            header: "Action",
            cell: ({ row }) => {
                const item = row.original;
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Row actions">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onAttachProof(item)}>Attach Proof</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
            meta: { sticky: "right" },
        },
    ], []);

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="6" />
            <div className="mb-4 flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate("/vendor")}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-lg font-semibold text-foreground">
                    Vendor Debts{ledgerResult?.vendorName ? ` — ${ledgerResult.vendorName}` : ""}
                </h1>
                {ledgerResult?.vendorCode && (
                    <Badge variant="secondary" className="whitespace-nowrap">{ledgerResult.vendorCode}</Badge>
                )}
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border p-4">
                    <div className="text-xs text-muted-foreground">Total Debt</div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                        {formatAmount(ledgerResult?.totalDebt)}
                    </div>
                </div>
                <div className="rounded-xl border p-4">
                    <div className="text-xs text-muted-foreground">Paid</div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                        {formatAmount(ledgerResult?.totalPaid)}
                    </div>
                </div>
                <div className="rounded-xl border p-4">
                    <div className="text-xs text-muted-foreground">Unpaid</div>
                    <div className={`mt-1 text-xl font-semibold tabular-nums ${outstanding > 0 ? "text-destructive" : "text-foreground"}`}>
                        {formatAmount(outstanding)}
                    </div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={ledgerRows}
                loading={getVendorDebtLedgerOnLoad}
                title="Transactions"
                headerActions={<Button onClick={openEntry}>Add Entry</Button>}
                toolbar={
                    <Select value={entryTypeFilter || "ALL"} onValueChange={onEntryTypeFilterChange}>
                        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All</SelectItem>
                            <SelectItem value={ENTRY_TYPE_DEBT}>Debt</SelectItem>
                            <SelectItem value={ENTRY_TYPE_PAYMENT}>Payment</SelectItem>
                        </SelectContent>
                    </Select>
                }
                highlightRowKey={highlightDebtId}
                getRowKey={(row: any) => row?.vendorDebtId}
                pagination={{
                    page: ledgerPage?.currentPage || 1,
                    total: ledgerPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />

            <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Vendor Debt Entry</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Select value={entryType} onValueChange={setEntryType}>
                            <SelectTrigger><SelectValue placeholder="Entry Type" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ENTRY_TYPE_DEBT}>Debt — amount owed to vendor</SelectItem>
                                <SelectItem value={ENTRY_TYPE_PAYMENT}>Payment — amount repaid</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input
                            type="number"
                            min={0}
                            max={entryType === ENTRY_TYPE_PAYMENT ? outstanding : undefined}
                            placeholder="Amount"
                            value={entryAmount}
                            onChange={(e) => setEntryAmount(e.target.value)}
                        />
                        {entryType === ENTRY_TYPE_PAYMENT && (
                            <p className="text-xs text-muted-foreground">
                                {outstanding > 0
                                    ? `Unpaid debt: ${formatAmount(outstanding)} — a payment cannot exceed this.`
                                    : "No unpaid debt against this vendor."}
                            </p>
                        )}
                        <Input
                            type="date"
                            max={todayStr()}
                            value={entryDate}
                            onChange={(e) => setEntryDate(e.target.value)}
                        />
                        <Input
                            placeholder="Notes (optional)"
                            value={entryNotes}
                            onChange={(e) => setEntryNotes(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEntryOpen(false)}>Cancel</Button>
                        <Button disabled={createVendorDebtEntryOnLoad} onClick={submitEntry}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <VendorDebtAttachmentViewer
                open={proofViewerOpen}
                onClose={() => setProofViewerOpen(false)}
                entityId={proofEntityId}
            />
            <VendorDebtAttachmentSheet
                open={proofSheetOpen}
                onClose={() => setProofSheetOpen(false)}
                entityId={proofEntityId}
            />
        </div>
    );
};

export default VendorDebtLedger;
